// Adapter: password field with a show/hide toggle over the hx input.
//
// Typing a strong password blind — then typing it again into a confirm field,
// also blind — is how people end up locked out of their own account. Every
// password field in /ops uses this so the value can be checked before submit.
//
//  - The toggle is a `button` with `tabIndex={-1}`: reachable by pointer, but
//    it doesn't sit between the field and the submit button in the tab order.
//  - Revealing is per-field and resets on unmount; nothing is remembered.
//  - `aria-pressed` + a live label so screen readers announce the state.
//  - The wrapper's positioning is inline so the toggle also sits right on the
//    auth pages, which render outside the `.helix` scope; the colour is a
//    token and simply inherits where the token is not defined.
import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "./input";

const PasswordInput = React.forwardRef<HTMLInputElement, Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">>(
  ({ className, style, ...props }, ref) => {
    const [shown, setShown] = React.useState(false);
    return (
      <span className="hx-password" style={{ position: "relative", display: "block", width: "100%" }}>
        <Input {...props} ref={ref} type={shown ? "text" : "password"} className={className} style={{ paddingRight: 36, ...style }} />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShown((s) => !s)}
          aria-pressed={shown}
          aria-label={shown ? "Hide password" : "Show password"}
          title={shown ? "Hide password" : "Show password"}
          className="hx-eye"
          style={{
            position: "absolute", top: 0, right: 0, bottom: 0, width: 36,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            border: "none", background: "transparent", padding: 0, boxShadow: "none",
            color: "var(--ink-3)", cursor: "pointer",
          }}
        >
          {shown ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </span>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
